import { defaults } from "ol/control/defaults";
import Feature, { FeatureLike } from "ol/Feature.js";
import TileLayer, { SourceType } from "ol/layer/WebGLTile";
import { Modify } from "ol/interaction.js";
import { Vector as VectorLayer } from "ol/layer";
import View from "ol/View.js";

// @ts-ignore
import { RecordType } from "../../../api/consts.js";
import {
  createFeatureForMeasure,
  getMeasureFromSerialized,
  // @ts-ignore
} from "./tiffDrawHelpers.ts";
// @ts-ignore
import { featureStyleFunction } from "./TiffStyles.ts";
import { Geometry } from "ol/geom.js";
import VectorSource from "ol/source/Vector.js";
import Collection from "ol/Collection.js";
import { MutableRefObject } from "react";
import Map from "ol/Map.js";
import {
  Measure,
  TiffViewOptions,
} from "../../../utils/types/TiffViewerTypes.js";
import { RecordResponseType } from "../../../utils/types/api/RecordResponseType.js";

// Helper function to set hidden property for measures
export function setHiddenForRecords(features: Feature[], measures: Measure[]) {
  features.forEach((feature) => {
    if (feature.get("isRecord") === true) {
      feature.set("hidden", true);
    } else {
      const measure = measures.find((m) => m.id === feature.getId());
      feature.set("hidden", measure ? measure.hidden === true : false);
    }
  });
}

// Helper function to set hidden property for records
export function setHiddenForMeasures(features: Feature[]) {
  features.forEach((feature) => {
    if (feature.get("isRecord") !== true) {
      feature.set("hidden", true);
    } else {
      feature.set("hidden", false);
    }
  });
}

export function setHideNonSolarPanels(features: Feature[]) {
  features.forEach((feature) => {
    if (feature.get("isSolarPanel") !== true) {
      feature.set("hidden", true);
    }
  });
}

// Helper function to remove all record features
export function removeRecordFeatures(
  vectorSource: VectorSource<Geometry>,
  features: Feature[],
) {
  features.forEach((feature) => {
    if (feature.get("isRecord")) vectorSource.removeFeature(feature);
  });
}

export function removeMeasureFeatures(
  vectorSource: VectorSource<Geometry>,
  features: Feature[],
) {
  features.forEach((feature) => {
    if (feature.get("isRecord") !== true) vectorSource.removeFeature(feature);
  });
}

export function addNewMeasureFeatures(
  vectorSource: VectorSource<Geometry>,
  measures: Measure[],
) {
  measures.forEach((measure) => {
    const converted = getMeasureFromSerialized(measure);
    let feature;
    if (measure.isSolarPanel) {
      feature = createFeatureForMeasure(converted, "SOLAR_PANEL");
    } else {
      feature = createFeatureForMeasure(converted, "measure");
    }
    vectorSource.addFeature(feature);
    feature.set("toBeStored", converted.toBeStored);
    feature.set("dbId", converted.dbId);
  });
}

// Helper function to add new features
export function addNewRecordFeatures(
  vectorSource: VectorSource<Geometry>,
  records: RecordResponseType[],
) {
  records.forEach((record) => {
    const measure = getMeasureFromSerialized({
      ...record,
      type: record.measure_type,
      points: record.points,
    });

    const feature = createFeatureForMeasure(measure, "record");
    feature.set("toBeStored", record.toBeStored);
    feature.set("dbId", record.dbId);
    feature.set("showValue", record.type === RecordType.AREA);
    vectorSource.addFeature(feature);
  });
}

// Helper function to assign a unique id to a feature if not already assigned
export function assignUniqueIdToFeature(feature: Feature) {
  if (!feature.getId()) {
    feature.setId(crypto.randomUUID());
  }
}

// Helper function to assign a name to a feature if not already assigned
export function assignNameToFeature(feature: Feature, nextFeatureName: string) {
  if (!feature.get("name")) {
    feature.set("name", nextFeatureName);
  }
}

// Helper function to add a change listener to a feature
export function addChangeListenerToFeature(
  feature: Feature,
  onChangeFeature: (feature: Feature<Geometry>) => void,
) {
  feature.on("change", (e) => {
    onChangeFeature(e.target);
  });
}

// Helper function to mark a feature for modification if required
export function markFeatureForModificationIfNeeded(
  feature: Feature,
  modifyFeatures: MutableRefObject<Collection<Feature<Geometry>>>,
) {
  if (!feature.get("toBeStored")) {
    modifyFeatures.current.push(feature);
  }
}

// Helper function to add extra resolutions
export function extendResolutions(viewOptions: TiffViewOptions) {
  const lastResolution =
    viewOptions.resolutions[viewOptions.resolutions.length - 1];

  return [
    ...viewOptions.resolutions,
    lastResolution * 0.5,
    lastResolution * 0.25,
    lastResolution * 0.125,
  ];
}

type TooltipState = {
  position: number[]; // [x, y] pixel coords
  text: string;
};

// Helper function to create and configure the map
export function createAndConfigureMap(
  mapView: View,
  tifSource: SourceType,
  vectorSource: VectorSource<Geometry>,
  modifyFeatures: MutableRefObject<Collection<Feature<Geometry>>>,
  setTooltip: (tooltip: TooltipState | null) => void,
  t: (translationKey: string) => string,
) {
  const map = new Map({
    layers: [
      new TileLayer({
        source: tifSource,
      }),
      new VectorLayer({
        source: vectorSource,
        style: (feature) => featureStyleFunction({ feature, t }),
      }),
    ],
    controls: defaults({ attribution: false, zoom: false }),
    view: mapView,
  });

  const modifyInteraction = new Modify({
    features: modifyFeatures.current,
    insertVertexCondition: () => false,
  });

  map.addInteraction(modifyInteraction);

  map.on("pointermove", (event: any) => {
    setTooltip(null); // Hide any previous tooltip
    map.forEachFeatureAtPixel(event.pixel, (feature: FeatureLike) => {
      const name = feature.get("name");

      if (feature.get("hidden") === true) return;
      if (!name) return;

      setTooltip({
        position: event.pixel,
        text: feature.get("name"),
      });
    });
  });

  return { map, modifyInteraction };
}
